import React, { useState, useEffect, useRef, ReactNode } from "react";

import {
	Grid,
	Card,
	CardContent,
	CardHeader,
	Typography,
	Checkbox,
	MenuItem,
	ListItemText,
	Button,
	ClickAwayListener,
	Popper,
	MenuList,
	Paper,
	Grow,
	IconButton
} from "@mui/material";

import ReactTable, {
	TableProps,
	Column,
	SortingRule,
	Filter,
	Resize,
	SubComponentFunction
} from "react-table";
import "react-table/react-table.css";

import { exportExcel } from "./export";

import useStyles from "./styles";
import { FaRegFileExcel } from "react-icons/fa";

interface Props extends Partial<TableProps> {
	title: React.ReactNode;
	titleText?: string | any;
	data: any[];
	tableRef?: React.MutableRefObject<any>;
	visibleColumns?: string[];
	columns: any[];
	headerRightComponent?: React.ReactNode;
	onRowClicked?(v: any): void;
}

export interface ReportStateProps extends Partial<TableProps> {
	page: number;
	pageSize: number;
	pages: number;
	sorting: number;
	sorted: SortingRule[];
	filtered: Filter[];
	resized: Resize[];
	SubComponent: SubComponentFunction;
	toExport: boolean;
	callback: (err: any, data: any) => void;
}

export interface ReportProps extends ReactTable<ReportStateProps> {
	state: ReportStateProps;
}

function Report({
	title,
	titleText,
	data,
	columns,
	tableRef,
	headerRightComponent,
	onRowClicked,
	getTdProps,
	visibleColumns,
	...rest
}: Props): JSX.Element {
	const classes = useStyles({});

	if (!titleText && typeof title === "string") titleText = title;

	const [showColumns, setShowColumns] = useState(
		visibleColumns && visibleColumns.length > 0
			? visibleColumns
			: columns.filter(c => typeof c.Header === "string").map(c => c.Header)
	);

	const [showColumnsMenu, setShowColumnsMenu] = useState(false);
	const columnsMenuAnchor = useRef(null);
	const [rColumns, setRColumns] = useState(columns);
	const table = useRef(null);

	useEffect(() => {
		for (const column of columns) {
			column.show = showColumns.indexOf(column.Header.toString()) > -1;
		}

		setRColumns([...columns]);
	}, [showColumns, columns]);

	const handleShowColumnsChange = (c: Column<any>) => {
		setShowColumns((prev: any) => {
			const headerValue = c.Header;
			console.log(c)
			if (headerValue !== null && headerValue !== undefined) {
				const value = headerValue.toString();

				if (prev.indexOf(value) > -1)
					return prev.filter((p: any) => p !== value);

					prev.push(value);
			}
			
			return [...prev];
		});
	};

	const handleListKeyDown = (event: any) => {
		if (event.key === "Tab") {
			event.preventDefault();
			setShowColumnsMenu(false);
		}
	};

	const handleExport = () => {
		exportExcel(tableRef || table, rColumns, titleText);
	};

	const renderHeaderRightComponent = () => {
		if (headerRightComponent) {
			if (!visibleColumns || !visibleColumns.length)
				return headerRightComponent;
		}

		return (
			<div
				style={{
					alignItems: "center",
					display: "flex"
				}}
			>
				{headerRightComponent}
				<IconButton onClick={handleExport}>
					<FaRegFileExcel />
				</IconButton>
				<Button
					ref={columnsMenuAnchor}
					aria-controls={showColumnsMenu ? "column-menu-list-grow" : undefined}
					aria-haspopup="true"
					onClick={e => setShowColumnsMenu(prev => !prev)}
				>
					Colunas
				</Button>

				<Popper
					open={showColumnsMenu}
					anchorEl={columnsMenuAnchor.current}
					role={undefined}
					placement="bottom-end"
					transition
				>
					{({ TransitionProps, placement }) => (
						<Grow
							{...TransitionProps}
							style={{
								transformOrigin: "right top"
							}}
						>
							<Paper
								style={{
									maxHeight: 400,
									overflowY: "auto"
								}}
							>
								<ClickAwayListener
									onClickAway={() => setShowColumnsMenu(false)}
								>
									<MenuList
										autoFocusItem={showColumnsMenu}
										id="column-menu-list-grow"
										onKeyDown={handleListKeyDown}
									>
										{columns
											.filter(c => typeof c.Header === "string")
											.map((c, i) => (
												<MenuItem
													key={i}
													value={c.id}
													onClick={() => handleShowColumnsChange(c)}
												>
													<Checkbox
														checked={
															showColumns.indexOf(c.Header.toString()) > -1
														}
													/>
													<ListItemText primary={c.Header.toString()} />
												</MenuItem>
											))}
									</MenuList>
								</ClickAwayListener>
							</Paper>
						</Grow>
					)}
				</Popper>
			</div>
		);
	};

	return (
		<Grid container>
			<Grid item xs={12}>
				<Card>
					<CardHeader
						title={<Typography variant="h6">{title}</Typography>}
						classes={{ action: classes.action }}
						action={renderHeaderRightComponent()}
					/>
					<CardContent className={classes.cardContent}>
						<ReactTable
							className="-striped -highlight"
							data={data}
							ref={tableRef || table}
							columns={rColumns}
							defaultPageSize={10}
							pageSizeOptions={[5, 10, 15, 20, 25, 50, 100]}
							getTdProps={(state: any, rowInfo: any, column: any) => {
								let props: object | undefined = undefined;
								if (getTdProps) {
									props = getTdProps(state, rowInfo, column);
								}
								return {
									...props,
									style: {
										display: "flex",
										alignItems: "center",
										cursor:
											onRowClicked && !(column && column.id === "actions")
												? "pointer"
												: undefined
									},
									onClick: (e: any, handleOriginal: any) => {
										if (rowInfo && !(column && column.id === "actions")) {
											if (onRowClicked) {
												onRowClicked(rowInfo.original);
											} else {
												handleOriginal && handleOriginal(e);
											}
										}
									}
								};
							}}
							{...rest}
						/>
					</CardContent>
				</Card>
			</Grid>
		</Grid>
	);
}

export default Report;
